import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { faqSearch, faqWords } from "@/lib/faqSearch";
import { Question, X, MagnifyingGlass, CaretDown, ArrowRight } from "@phosphor-icons/react";

export const FaqBubble = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [scrolling, setScrolling] = useState(false);
  const [ribbon, setRibbon] = useState(false);
  const input = useRef(null);
  const root = useRef(null);

  useEffect(() => {
    const check = () => setRibbon(!!document.querySelector('[data-testid="joke-ribbon"]'));
    check();
    const t = setInterval(check, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let t;
    const onScroll = () => { setScrolling(true); clearTimeout(t); t = setTimeout(() => setScrolling(false), 600); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); clearTimeout(t); };
  }, []);

  useEffect(() => {
    if (!open) return;
    if (!items) api.get("/faq").then((r) => setItems(r.data.items)).catch(() => setItems([]));
    setTimeout(() => input.current?.focus(), 50);
  }, [open, items]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onDown = (e) => root.current && !root.current.contains(e.target) && setOpen(false);
    window.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => { window.removeEventListener("keydown", onKey); document.removeEventListener("pointerdown", onDown); };
  }, [open]);

  const terms = useMemo(() => faqWords(q), [q]);
  const results = useMemo(() => (items ? (terms.length ? faqSearch(items, terms) : items).slice(0, 4) : []), [items, terms]);

  return (
    <div ref={root}>
      {open && (
        <div data-testid="faq-bubble-panel" role="dialog" aria-label="Quick help"
          className="fixed z-50 inset-x-0 bottom-0 max-h-[55vh] rounded-t-lg md:inset-x-auto md:left-4 md:bottom-16 md:w-[320px] md:max-h-[min(420px,60vh)] md:rounded-md flex flex-col bg-white border border-border shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-200">
          <div className="px-4 pt-3 pb-3 border-b border-border">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-base">Quick answers</h2>
              <button type="button" data-testid="faq-bubble-close" onClick={() => setOpen(false)} aria-label="Close help" className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <div className="relative mt-2">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input ref={input} data-testid="faq-bubble-search" value={q} onChange={(e) => { setQ(e.target.value); setExpanded(null); }}
                placeholder="Shipping, sizing, refunds…" className="w-full h-10 pl-9 pr-3 rounded-sm border border-border text-sm focus:outline-none focus:border-[#C84A31]" />
            </div>
          </div>
          <ul data-testid="faq-bubble-results" className="overflow-y-auto divide-y divide-border">
            {!items && <li className="p-4 text-sm text-muted-foreground">Loading…</li>}
            {results.map((i) => (
              <li key={i.id}>
                <button type="button" data-testid={`faq-bubble-item-${i.id}`} onClick={() => setExpanded(expanded === i.id ? null : i.id)}
                  className="w-full text-left px-4 py-3 text-sm font-semibold flex justify-between gap-2 hover:bg-muted/50">
                  {i.question}<CaretDown size={14} className={`shrink-0 mt-0.5 transition-transform ${expanded === i.id ? "rotate-180" : ""}`} />
                </button>
                {expanded === i.id && <p data-testid="faq-bubble-answer" className="px-4 pb-3 text-sm text-foreground/80 leading-relaxed whitespace-pre-line">{i.answer}</p>}
              </li>
            ))}
            {items && terms.length > 0 && !results.length && <li data-testid="faq-bubble-empty" className="p-4 text-sm">No quick answer for that yet.</li>}
          </ul>
          <Link to={`/faq${q ? `?q=${encodeURIComponent(q)}` : ""}`} data-testid="faq-bubble-more" onClick={() => setOpen(false)}
            className="px-4 py-3 border-t border-border text-sm text-[#C84A31] flex items-center justify-between hover:bg-muted/50">
            {items && terms.length > 0 && !results.length ? "Ask us your question" : "See all FAQs"} <ArrowRight size={14} />
          </Link>
        </div>
      )}
      <button type="button" data-testid="faq-bubble-btn" onClick={() => setOpen((v) => !v)} aria-label={open ? "Close help" : "Open quick help"}
        className={`fixed z-50 right-3 md:right-auto md:left-4 md:bottom-4 ${ribbon ? "bottom-[6rem]" : "bottom-3"} h-9 px-3.5 rounded-full bg-[#2A4B3C]/95 text-white text-sm font-semibold shadow-lg inline-flex items-center gap-1.5 hover:bg-[#2A4B3C] active:scale-95 transition-[opacity,transform,bottom] duration-300 ${open ? "max-md:hidden" : ""} ${scrolling && !open ? "opacity-0 translate-y-3 pointer-events-none" : "opacity-100"}`}>
        {open ? <X size={15} weight="bold" /> : <Question size={16} weight="bold" />} {open ? "Close" : "Help"}
      </button>
    </div>
  );
};
